/**
 * Purpose: Top-level React component for the ada portal home page.
 *          Renders two grouped card lists: "Demos" (visible to every
 *          logged-in user) and "Admin tools" (visible only to users
 *          holding the `admin` Zitadel project role).
 *
 * Implementation notes:
 *   - No client-side router and no /api calls. Visibility comes from the
 *     OIDC ID-token role claim via `useHasRole('admin')`.
 *   - The link list is hard-coded — each entry corresponds to an admin/
 *     UI Fastify plugin mounted by apps/api. Adding a new one is a
 *     single push into the relevant array below plus its mount in
 *     apps/api/src/server.ts.
 *   - The card chrome (rounded border, hover ring, icon row) matches
 *     the rest of the ada admin SPAs for visual continuity.
 *   - Admin-only cards are still rendered as a section so admins see a
 *     single home page covering both demo + operator surfaces; the
 *     admin section is simply hidden for non-admins.
 *
 * Created: 2026-05-12 10:00
 *
 * Changelog:
 *   - 2026-05-12 10:00 — Initial version.
 *   - 2026-05-13 12:00 — Keep /admin/* hrefs without trailing slashes.
 *                        Real fix landed in each admin SPA's vite config
 *                        (relative → absolute base) plus a bare-path
 *                        handler in apps/api/src/server.ts; mirrors the
 *                        existing /portal and /knowledge mounts.
 *   - 2026-05-13 23:30 — Add `/admin/users` + `/admin/tenants` as
 *                        disabled placeholder cards (no SPA yet — they
 *                        flag the post-beta backlog). Switch the live
 *                        admin hrefs to trailing-slash form because the
 *                        bare-URL handler in apps/api/src/server.ts
 *                        currently 404s under fastify-static's prefix
 *                        routing (works for /portal, fails for nested
 *                        /admin/* prefixes — separate follow-up).
 *   - 2026-05-14 11:00 — Drop the @ada/copilot-widget Copilot panel for
 *                        admins. The widget is wired to the new
 *                        `api_list_methods` + `api_call_method` server
 *                        tools, giving admins a chat-driven entry point
 *                        to the entire /api surface from the portal
 *                        landing. Gated behind `isAdmin` so only users
 *                        with the Zitadel admin role see the panel —
 *                        the dispatcher still enforces per-method roles
 *                        on every tool call, but matching the surface
 *                        to the audience keeps the UI clean.
 *   - 2026-05-14 07:45 — Pin model="anthropic-text-mid". The widget's
 *                        copilot-core default ("anthropic-model-mid")
 *                        does not exist in the deployed DDB model
 *                        catalogue, which uses the `anthropic-text-*` /
 *                        `anthropic-thinking-*` naming. The alias is
 *                        present in the local seed-catalog.ts but never
 *                        synced to dev DDB. Passing an explicit alias
 *                        unblocks the portal without changing copilot-
 *                        core defaults or touching the catalogue.
 *   - 2026-06-11 12:05 — Enable the Users + Tenants admin cards (drop the
 *                        `disabled` "Soon" pill, point at /admin/users/ and
 *                        /admin/tenants/) now that those SPAs ship.
 */

import { Copilot } from '@ada/copilot-widget';
import { getAccessToken, useAuth, useHasRole } from '@ada/auth-react';
import {
  BookOpen,
  Building2,
  CreditCard,
  Database,
  KeyRound,
  ListChecks,
  Sparkles,
  Users,
  type LucideIcon,
} from 'lucide-react';

interface PortalLink {
  /**
   * Target URL. For live admin SPAs use the trailing-slash form
   * (`/admin/x/`) — the bare-prefix handler in apps/api currently
   * 404s for nested `/admin/*` mounts.
   */
  href: string;
  title: string;
  description: string;
  icon: LucideIcon;
  /**
   * Render as a non-clickable card with a "Soon" pill. Used for
   * surfaces whose backend module exists but whose admin SPA hasn't
   * been built yet (users / tenants as of 2026-05-13).
   */
  disabled?: boolean;
}

// Visible to every logged-in user, regardless of role.
const PUBLIC_LINKS: ReadonlyArray<PortalLink> = [
  {
    href: '/knowledge',
    title: 'Knowledge & Vector',
    description:
      'Drag-drop ingest, semantic search and RAG chat. Powered by ' +
      'the @ada/knowledge + @ada/vector modules.',
    icon: Database,
  },
  {
    href: '/api',
    title: 'API Reference',
    description:
      'Browse every JSON-RPC method exposed at POST /api, with request ' +
      'and response shapes.',
    icon: BookOpen,
  },
];

// Visible only when the current user holds the `admin` Zitadel role.
const ADMIN_LINKS: ReadonlyArray<PortalLink> = [
  {
    href: '/admin/credits/',
    title: 'Credits',
    description:
      'Inspect and adjust per-tenant credit allowances and consumption.',
    icon: CreditCard,
  },
  {
    href: '/admin/api-keys/',
    title: 'API Keys',
    description:
      'Mint and revoke long-lived ada API keys (Zitadel-backed PATs). ' +
      'Requires the target machine user to already exist — provision ' +
      'one via /admin/users.',
    icon: KeyRound,
  },
  {
    href: '/admin/trace/',
    title: 'Trace',
    description:
      'Search dispatcher trace records, drill into spans, and inspect ' +
      'latency stats.',
    icon: ListChecks,
  },
  {
    href: '/admin/users/',
    title: 'Users',
    description:
      'Provision Zitadel machine/human users and manage their ada roles ' +
      'per tenant. Backed by the @ada/users module.',
    icon: Users,
  },
  {
    href: '/admin/tenants/',
    title: 'Tenants',
    description:
      'Create tenant orgs and inspect their users. ' +
      'Backed by the @ada/tenants module.',
    icon: Building2,
  },
];

export function App() {
  const { user, logout } = useAuth();
  const isAdmin = useHasRole('admin');

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-indigo-600" aria-hidden />
            <h1 className="text-xl font-semibold tracking-tight">ada · portal</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-600">
              {user?.email ?? user?.sub}
            </span>
            {isAdmin ? (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-indigo-50 text-indigo-700">
                admin
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => void logout()}
              className="px-2 py-1 rounded border border-slate-300 text-xs text-slate-700 hover:bg-slate-50 transition-colors"
            >
              sign out
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-5xl w-full mx-auto px-6 py-8 space-y-10">
        <Section
          title="Demos"
          subtitle="Tenant-facing surfaces, available to every signed-in user."
          links={PUBLIC_LINKS}
        />
        {isAdmin ? (
          <Section
            title="Admin tools"
            subtitle="Operator consoles. Restricted to users with the admin role."
            links={ADMIN_LINKS}
          />
        ) : null}
      </main>
      {isAdmin ? (
        <Copilot
          appId="portal"
          auth={{ kind: 'bearer', getToken: getAccessToken }}
          model="anthropic-text-mid"
          title="ada Copilot"
          subtitle="ask anything — I can read docs and call any /api method on your behalf"
          systemHints={
            'You are embedded in the ada portal as an admin assistant. ' +
            'Discover the platform with `api_list_methods` (optionally filter by namespace or search), ' +
            'read deeper context with `docs_get`, then invoke any JSON-RPC method via `api_call_method`. ' +
            'Roles and tenant scope are enforced by the platform — when a call fails with an auth error, ' +
            'explain it to the user rather than retrying. Prefer read-only methods unless the user explicitly ' +
            'asks for a write; for destructive calls (delete/revoke/etc.) confirm first by quoting the exact ' +
            'method name and params you intend to use.'
          }
          serverTools={['docs_get', 'api_list_methods', 'api_call_method']}
        />
      ) : null}
    </div>
  );
}

function Section({
  title,
  subtitle,
  links,
}: {
  title: string;
  subtitle: string;
  links: ReadonlyArray<PortalLink>;
}) {
  return (
    <section>
      <div className="mb-4">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
          {title}
        </h2>
        <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {links.map((link) => (
          <Card key={link.href} link={link} />
        ))}
      </div>
    </section>
  );
}

function Card({ link }: { link: PortalLink }) {
  const Icon = link.icon;
  const body = (
    <div className="flex items-start gap-3">
      <div
        className={
          link.disabled
            ? 'rounded-md bg-slate-100 p-2 text-slate-400'
            : 'rounded-md bg-indigo-50 p-2 text-indigo-600 group-hover:bg-indigo-100 transition-colors'
        }
      >
        <Icon className="h-4 w-4" aria-hidden />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3
            className={
              link.disabled
                ? 'text-sm font-semibold text-slate-500'
                : 'text-sm font-semibold text-slate-900'
            }
          >
            {link.title}
          </h3>
          {link.disabled ? (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-amber-50 text-amber-700">
              soon
            </span>
          ) : (
            <span className="text-[10px] font-mono text-slate-400">
              {link.href}
            </span>
          )}
        </div>
        <p
          className={
            link.disabled
              ? 'text-xs text-slate-500 mt-1 leading-relaxed'
              : 'text-xs text-slate-600 mt-1 leading-relaxed'
          }
        >
          {link.description}
        </p>
      </div>
    </div>
  );

  // Disabled cards: render as a non-clickable div, no hover affordance.
  // Live cards: <a> with the standard hover ring.
  if (link.disabled) {
    return (
      <div
        aria-disabled="true"
        className="block rounded-lg border border-dashed border-slate-200 bg-slate-50/50 p-4 cursor-not-allowed"
        title="Backend module exists; admin SPA is on the post-beta backlog."
      >
        {body}
      </div>
    );
  }
  return (
    <a
      href={link.href}
      className="group block rounded-lg border border-slate-200 bg-white p-4 transition-all hover:border-indigo-300 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
    >
      {body}
    </a>
  );
}
